import React from 'react'
import styled from 'styled-components'

import { getImage } from 'gatsby-plugin-image'
import { usePlaceholderImage } from '../../hooks/placeholderImage'
import { ExtendedH3, ExtendedH6 } from '../Typography/Typography'
import theme from '../atoms/theme'
import { StyledImg } from '../organisms/contentSet'
import { CategorySeparator } from '../organisms/heroCarousel'
import { ContentSetItemStyled } from '../organisms/pageCategoryGroupContentSetWithImages'
import {
  CategoryBlock,
  CategoryLinks,
  ContentSetItemContent,
  ContentSetItemWrapper,
  StyledArrowLinkInContent,
} from './contentSetItemEvent'

const { breakpoints } = theme

type ContentSetItemPageProps = {
  data: {
    node?: object
  }
  isSearchResult?: boolean
}

type CategoryGroupContentSetItemWrapperProps = {
  hasHeroImage: boolean
}

const CategoryGroupContentSetItemWrapper = styled<{ isActive: boolean }>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ hasHeroImage, ...rest }) => <ContentSetItemWrapper {...rest} />,
)<CategoryGroupContentSetItemWrapperProps>`
  text-decoration: none;
  display: block;
  margin-bottom: 2.5rem;

  @media only screen and ${breakpoints.fromNormalScreen} {
    display: ${(props): string => (props.hasHeroImage ? 'block' : 'flex')};
    width: calc(1 / 3 * 100% - (1 - 1 / 3) * 2.5rem);
    margin-right: 2.5rem;
  }

  &:nth-child(3n) {
    margin-right: 0;
  }
`

const ContentSetLeadingParagraph = styled.p`
  margin-top: 1rem;
  font-size: 1.4rem;
`

const ContentSetItemPage = (props: ContentSetItemPageProps): JSX.Element => {
  const {
    id,
    title: itemTitle,
    category,
    slug,
    heroImage,
    imageWithFocalPoint,
    leadingParagraph,
    url,
    pageCategoryParent,
  } = props.data.node || props.data
  const placeholderImageData = usePlaceholderImage()

  let computedCategory = category

  if (computedCategory === undefined && pageCategoryParent) {
    computedCategory = pageCategoryParent
  }

  let categoryColour = 'transparent'

  if (computedCategory?.colour) {
    categoryColour = computedCategory.colour
  }

  if (computedCategory?.pageCategoryParent) {
    categoryColour = computedCategory.pageCategoryParent.colour
  }

  let categoryTitle = null

  if (computedCategory?.title) {
    categoryTitle = computedCategory.title
  }

  if (computedCategory?.pageCategoryParent) {
    categoryTitle = computedCategory.pageCategoryParent.title
  }

  let linkSlug = `/${slug}/`

  if (computedCategory?.slug) {
    linkSlug = `/${computedCategory.slug}/${slug}/`
  }

  if (computedCategory?.pageCategoryParent) {
    linkSlug = `/${computedCategory.pageCategoryParent.slug}${linkSlug}`
  }

  if (url) {
    linkSlug = url
  }

  return (
    <CategoryGroupContentSetItemWrapper
      to={linkSlug}
      key={id}
      hasHeroImage={heroImage !== null || props.isSearchResult === true}
    >
      <ContentSetItemStyled>
        {imageWithFocalPoint?.image || heroImage ? (
          <StyledImg
            className='image'
            image={getImage(imageWithFocalPoint?.image) || getImage(heroImage)}
            alt={heroImage?.title}
          />
        ) : (
          <StyledImg
            className='image'
            image={placeholderImageData.image.childImageSharp.gatsbyImageData}
            alt={placeholderImageData.image.title}
          />
        )}
        <ContentSetItemContent
          className='content-set-item-content'
          colour={categoryColour}
        >
          <CategoryBlock colour={categoryColour}>
            <ExtendedH6 className='category'>
              <CategoryLinks>
                {categoryTitle && (
                  <CategorySeparator as='span'>
                    {categoryTitle}
                  </CategorySeparator>
                )}
              </CategoryLinks>
            </ExtendedH6>
          </CategoryBlock>
          <ExtendedH3>{itemTitle}</ExtendedH3>
          {leadingParagraph && (
            <ContentSetLeadingParagraph>
              {leadingParagraph.childMarkdownRemark.rawMarkdownBody}
            </ContentSetLeadingParagraph>
          )}
          <StyledArrowLinkInContent>View more</StyledArrowLinkInContent>
        </ContentSetItemContent>
      </ContentSetItemStyled>
    </CategoryGroupContentSetItemWrapper>
  )
}

export default ContentSetItemPage
